import genericMemo from "Common/componentUtils/genericMemo";
import { MenuList } from "Components/menu";
import { manageOverlay } from "Components/overlaysContext/overlaysManager";
import withOverlaysContext from "Components/overlaysContext/withOverlaysContext";
import classNames from "classnames";
import { Popover } from "devextreme-react/popover";
import constant from "lodash/constant";
import filter from "lodash/filter";
import includes from "lodash/includes";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import join from "lodash/join";
import map from "lodash/map";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import ItemComponent from "../menu/menuList/innerMenuItem/innerMenuItem";
import locale from "./scrolledDropdown.locale";
import BtnDefaultToggler from "./toggler/btnDefaultToggler";
function ScrolledDropdownInner(props) {
    var _a, _b, _c;
    const [open, setOpen] = useState(false);
    const id = useMemo(() => props.id || `id${uuid()}`, [props.id]);
    const togglerId = `toggler_${id}`;
    const popoverId = `popover_${id}`;
    const defaultMenuRef = useRef();
    const menuRef = props.menuRef || defaultMenuRef;
    const wrapperAttr = useMemo(() => ({
        id: popoverId,
        height: "auto",
        class: "popover-as-dropdown",
        zIndex: props.zIndex
    }), [popoverId, props.zIndex]);
    const popoverRef = useRef(null);
    useEffect(() => () => {
        manageOverlay(popoverRef.current, props.overlaysManager, false);
    }, []);
    const getTitle = () => {
        const { items, selectedKey: selectedItemIds, keySelector, textSelector, multi } = props;
        if (isNil(selectedItemIds) || (multi && isEmpty(selectedItemIds)))
            return undefined;
        const selectedItems = filter(items, item => includes(isArray(selectedItemIds) ? selectedItemIds : [selectedItemIds], keySelector(item)));
        if (isEmpty(selectedItems))
            return undefined;
        return join(map(selectedItems, textSelector), ", ");
    };
    const changeOverlayShow = (newShow, ignoreIfNotModified) => {
        var _a;
        if (!ignoreIfNotModified && newShow === open)
            return;
        newShow && ((_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.setSearchText) && menuRef.current.setSearchText("");
        setOpen(newShow);
        manageOverlay(popoverRef.current, props.overlaysManager, newShow);
    };
    const handleMenuItemSelect = (selectedKey) => {
        if (props.multi) {
            props.onSelectedMultiChanged && props.onSelectedMultiChanged(selectedKey);
        }
        else {
            props.onSelectedChanged && props.onSelectedChanged(selectedKey);
            changeOverlayShow(false);
        }
    };
    const handleDropdownToggleClick = useCallback(() => {
        changeOverlayShow(!open);
    }, [open]);
    const handleHideMenuOverlay = useCallback(() => {
        changeOverlayShow(false, true);
    }, []);
    const handleCancelAndCloseDropdown = useCallback(() => {
        props.onCancel && props.onCancel();
        handleHideMenuOverlay();
    }, [props.onCancel]);
    const { togglerComponent: CustomTogglerComponent, itemComponent: CustomItemComponent } = props;
    const toggleProps = CustomTogglerComponent
        ? {
            disabled: props.disabled,
            maxLength: props.maxLength,
            noCaret: props.noCaret,
            title: props.title,
            caption: props.caption,
            content: props.togglerBtnContent,
            muteDefaultCaptionText: props.togglerMuteDefaultCaptionText,
            height: ((_a = CustomTogglerComponent === null || CustomTogglerComponent === void 0 ? void 0 : CustomTogglerComponent.props) === null || _a === void 0 ? void 0 : _a.height) || props.togglerHeight,
            className: classNames(props.togglerClassName, props.togglerNoWrapText ? "nowrap" : "normal-wrap", "text-left", (_b = CustomTogglerComponent === null || CustomTogglerComponent === void 0 ? void 0 : CustomTogglerComponent.props) === null || _b === void 0 ? void 0 : _b.className),
            selectedKeyCaption: getTitle(),
            onClick: handleDropdownToggleClick,
            id: togglerId,
            open,
            attrTitle: props.togglerTitle,
            validationErrorMessage: props.validationErrorMessage,
            validationStatus: props.validationStatus,
            zIndex: props.zIndex,
            label: props.label
        }
        : undefined;
    const handleApplySelect = useCallback(() => {
        var _a;
        if (!props.onApply) {
            return;
        }
        (_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.instance.hide();
        props.onApply();
    }, [props.onApply]);
    const menu = (React.createElement(MenuList, { menuRef: menuRef, height: props.height, maxHeight: props.maxHeight, minHeight: props.minHeight, maxWidth: props.maxWidth, minWidth: props.minWidth, items: props.items, itemComponent: CustomItemComponent, itemClassName: props.itemClassName, selectedKey: props.selectedKey, multi: props.multi, onSelectedChanged: handleMenuItemSelect, keySelector: props.keySelector, textSelector: props.textSelector, visibilitySelector: props.visibilitySelector, showSearchInputWhenItemsLengthIsGreaterThan: props.showSearchInputWhenItemsLengthIsGreaterThan, allMenuItem: props.allMenuItem, allMenuItemText: props.allMenuItemText, itemNoWrapText: props.itemNoWrapText, searchInputText: props.searchInputText, searchInputMargins: props.searchInputMargins, emptyMessage: props.emptyMessage, withApplyButton: props.withApplyButton, onApply: handleApplySelect, onCancel: handleCancelAndCloseDropdown, searchStylingMode: props.searchStylingMode }));
    const togglerWidth = (_c = document.getElementById(togglerId)) === null || _c === void 0 ? void 0 : _c.clientWidth;
    const style = useMemo(() => ({
        display: "inline-block",
        verticalAlign: "middle",
        maxWidth: !props.maxWidth ? "unset" : props.maxWidth,
        minWidth: !props.minWidth ? "unset" : props.minWidth,
        width: props.width && `${props.width}px`
    }), [props.maxWidth, props.minWidth, props.width]);
    return (React.createElement("div", { ref: props.wrapperRef, className: props.className, title: !props.togglerTitle && CustomTogglerComponent ? getTitle() : undefined, style: style },
        Boolean(CustomTogglerComponent) &&
            React.cloneElement(CustomTogglerComponent, {
                ...toggleProps
            }),
        CustomTogglerComponent ? (React.createElement(Popover, { ref: popoverRef, wrapperAttr: wrapperAttr, target: `#${togglerId}`, container: (props.getOverlaysContainer && props.getOverlaysContainer()) || document.body, position: props.position, visible: open, onHidden: handleHideMenuOverlay, hideOnParentScroll: props.hideOnParentScroll, hideOnOutsideClick: true, deferRendering: false, animation: undefined, minWidth: togglerWidth, maxWidth: props.maxWidth, width: props.popoverWidth },
            React.createElement("div", { className: "popover-content" }, menu))) : (React.createElement("div", { className: "popover-content" }, menu))));
}
const ScrolledDropdown = genericMemo(withOverlaysContext(ScrolledDropdownInner));
ScrolledDropdown.displayName = "ScrolledDropdown";
export default ScrolledDropdown;
ScrolledDropdown.defaultProps = {
    caption: locale.SELECT_ELEMENT,
    height: "auto",
    maxHeight: 200,
    minHeight: 36,
    maxWidth: 350,
    minWidth: 150,
    textSelector: (o) => o,
    keySelector: (o) => o,
    visibilitySelector: constant(true),
    showSearchInputWhenItemsLengthIsGreaterThan: 10,
    searchInputMargins: true,
    itemComponent: ItemComponent,
    itemNoWrapText: false,
    togglerComponent: React.createElement(BtnDefaultToggler, null),
    togglerNoWrapText: false,
    togglerMuteDefaultCaptionText: true,
    getOverlaysContainer: constant(undefined),
    allMenuItemText: locale.ALL,
    position: {
        my: "top left",
        at: "bottom left",
        collision: "fit flip"
    },
    hideOnParentScroll: true,
    multi: false
};
